/**
 * Phase 14.5 Step 3 — DegradationProfileEditor (composer host).
 *
 * Single host component used by both `/degradation-profiles/new` and
 * `/degradation-profiles/:profileId`. Holds the form state via
 * `react-hook-form` (host-only; the 5 sub-forms are controlled leaves
 * — invariant carried over from Step 2).
 *
 * Behavioural contract:
 *   • mode="create"  → on save, POST → navigate("/degradation-profiles")
 *   • mode="edit"    → on save, PUT(:id) → navigate("/degradation-profiles")
 *   • Friendly Default / Stress Reference preset buttons fetch the live
 *     seeded profiles (is_seeded=true) on mount and spread the matching
 *     profile's config into the form. No backend constants replicated.
 *   • Validation summary collects sub-form / required-field issues;
 *     submit disabled while non-empty.
 *   • Seeded profiles render in read-only state (page-level decision —
 *     pass `readOnly` from the detail page when `profile.is_seeded`).
 */
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Save, Sparkles } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import IMUSubForm from "@/components/degradation/IMUSubForm";
import RadarSubForm from "@/components/degradation/RadarSubForm";
import TrackQualityMarkovSubForm from "@/components/degradation/TrackQualityMarkovSubForm";
import DataStarvationSubForm from "@/components/degradation/DataStarvationSubForm";
import ModulationThresholdsSubForm from "@/components/degradation/ModulationThresholdsSubForm";
import degradationProfiles from "@/api/degradationProfiles";

// Reasonable starter values matching the Pydantic shape. Operators
// MUST customise via the sub-forms or via a preset; these are just
// sensible non-rejecting defaults so the form mounts without backend
// hitting `extra="forbid"` on any required field.
const STARTER_VALUES = {
    name: "",
    description: "",
    tags: [],
    imu: {
        gyro_bias_dps: [0, 0, 0],
        gyro_drift_dps_per_s: [0, 0, 0],
        accel_bias_mps2: [0, 0, 0],
        accel_drift_mps2_per_s: [0, 0, 0],
        sample_rate_hz: 100,
        seed: 0,
    },
    radar: {
        range_bias_m: 0,
        range_bias_drift_m_per_s: 0,
        bearing_bias_deg: 0,
        bearing_bias_drift_deg_per_s: 0,
        elevation_bias_deg: 0,
        elevation_bias_drift_deg_per_s: 0,
        range_noise_std_m: 0,
        bearing_noise_std_deg: 0,
        elevation_noise_std_deg: 0,
        update_rate_hz: 20,
        seed: 0,
    },
    track_quality: {
        transition_matrix: [
            [0.95, 0.04, 0.0, 0.01],
            [0.10, 0.85, 0.04, 0.01],
            [0.0, 0.05, 0.90, 0.05],
            [0.30, 0.10, 0.0, 0.60],
        ],
        hold_time_means_s: [10.0, 5.0, 3.0, 1.0],
        reacquisition_probability_per_tick: 0.05,
        tick_rate_hz: 10.0,
        seed: 0,
    },
    data_starvation: {
        dropout_rate: 0,
        dropout_burst_lambda: 1,
        blackout_windows: [],
        latency_ms_constant: 0,
        latency_ms_jitter_std: 0,
        seed: 0,
    },
    modulation_thresholds: {
        terminal_window_fraction: 0.30,
        latency_terminal_fraction: 0.05,
        gyro_drift_threshold_deg: 5.0,
        reacquire_window_s: 0.5,
        reacquire_bernoulli_p: 0.20,
        range_bias_uses_payload_radius: true,
        fixed_range_bias_threshold_m: 5.0,
    },
};

function _stripServerFields(profile) {
    if (!profile) return null;
    // Excludes id / tenant_id / created_by / created_at / updated_at /
    // profile_version / is_seeded — the server fills these.
    const {
        id: _id, tenant_id: _t, created_by: _cb, created_at: _ca,
        updated_at: _ua, profile_version: _pv, is_seeded: _is, ...rest
    } = profile;
    void _id; void _t; void _cb; void _ca; void _ua; void _pv; void _is;
    return rest;
}

function _validate(formValues) {
    const errs = [];
    if (!formValues?.name || !String(formValues.name).trim()) {
        errs.push("Name is required.");
    }
    if (formValues?.name && String(formValues.name).length > 120) {
        errs.push("Name must be ≤ 120 characters.");
    }
    return errs;
}

export default function DegradationProfileEditor({
    mode,            // "create" | "edit"
    profileId,       // present when mode === "edit"
    initialValue,    // seeded by the detail page when editing
    registry,        // /api/degradation-profiles/registry payload
    readOnly = false,
}) {
    const navigate = useNavigate();
    const [saveErr, setSaveErr] = useState(null);
    const [saving, setSaving] = useState(false);
    const [seededByName, setSeededByName] = useState({});

    const defaults = useMemo(() => {
        if (initialValue) {
            return { ...STARTER_VALUES, ..._stripServerFields(initialValue) };
        }
        return STARTER_VALUES;
    }, [initialValue]);

    const { control, setValue, getValues, reset } = useForm({ defaultValues: defaults });
    const formValues = useWatch({ control }) || defaults;

    // Re-seed form when initialValue changes (edit page fetched late).
    useEffect(() => {
        reset(defaults);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaults]);

    // Fetch seeded profiles for preset buttons.
    useEffect(() => {
        let cancelled = false;
        degradationProfiles
            .list({ page: 1, page_size: 100 })
            .then((body) => {
                if (cancelled) return;
                const items = Array.isArray(body?.items) ? body.items : [];
                const byName = {};
                for (const p of items) {
                    if (p?.is_seeded && p?.name) byName[p.name] = p;
                }
                setSeededByName(byName);
            })
            .catch(() => {
                /* preset buttons silently disable themselves when the
                   list fetch fails — non-fatal for the editor. */
            });
        return () => { cancelled = true; };
    }, []);

    const seededNames = Array.isArray(registry?.seeded_profile_names)
        ? registry.seeded_profile_names
        : [];
    const errs = _validate(formValues);
    const submitDisabled = readOnly || saving || errs.length > 0;

    const applyPreset = (presetName) => {
        const seeded = seededByName[presetName];
        if (!seeded) return;
        const stripped = _stripServerFields(seeded);
        // Preserve operator's currently-typed name unless it was empty.
        const carryName = formValues?.name?.trim() ? formValues.name : `${presetName} copy`;
        const next = { ...stripped, name: carryName };
        reset(next);
    };

    const onSave = async () => {
        if (submitDisabled) return;
        setSaveErr(null);
        setSaving(true);
        try {
            const payload = getValues();
            if (mode === "edit" && profileId) {
                await degradationProfiles.update(profileId, payload);
            } else {
                await degradationProfiles.create(payload);
            }
            navigate("/degradation-profiles");
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Save failed.";
            setSaveErr(detail);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="degradation-profile-editor" data-mode={mode}>
            {readOnly && (
                <Alert
                    data-testid="degradation-editor-readonly-banner"
                    className="border-amber-500/40 bg-amber-500/10"
                >
                    <AlertDescription className="text-amber-200">
                        This is a seeded profile — read-only. Duplicate it from the list page
                        to edit a copy.
                    </AlertDescription>
                </Alert>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                        Name
                    </Label>
                    <Input
                        value={formValues.name || ""}
                        onChange={(e) => setValue("name", e.target.value)}
                        placeholder="e.g. Friendly Default — variant"
                        disabled={readOnly}
                        data-testid="degradation-editor-name"
                    />
                </div>
                <div>
                    <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                        Description
                    </Label>
                    <Input
                        value={formValues.description || ""}
                        onChange={(e) => setValue("description", e.target.value)}
                        disabled={readOnly}
                        data-testid="degradation-editor-description"
                    />
                </div>
            </div>

            {!readOnly && seededNames.length > 0 && (
                <div
                    className="flex flex-wrap items-center gap-2"
                    data-testid="degradation-editor-presets"
                >
                    <span className="text-xs text-ux-text-3">Apply preset:</span>
                    {seededNames.map((nm) => {
                        const available = !!seededByName[nm];
                        return (
                            <Button
                                key={nm}
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => applyPreset(nm)}
                                disabled={!available}
                                data-testid={`degradation-editor-preset-${nm.replaceAll(" ", "_")}`}
                            >
                                <Sparkles className="size-3" />
                                {nm}
                            </Button>
                        );
                    })}
                </div>
            )}

            <Tabs defaultValue="imu" data-testid="degradation-editor-tabs">
                <TabsList>
                    <TabsTrigger value="imu" data-testid="degradation-tab-imu">IMU</TabsTrigger>
                    <TabsTrigger value="radar" data-testid="degradation-tab-radar">Radar</TabsTrigger>
                    <TabsTrigger value="track_quality" data-testid="degradation-tab-track">Track Quality</TabsTrigger>
                    <TabsTrigger value="data_starvation" data-testid="degradation-tab-starvation">Data Starvation</TabsTrigger>
                    <TabsTrigger value="modulation" data-testid="degradation-tab-modulation">Modulation Thresholds</TabsTrigger>
                </TabsList>
                <TabsContent value="imu">
                    <IMUSubForm
                        value={formValues.imu}
                        onChange={(v) => setValue("imu", v)}
                        registry={registry?.imu}
                    />
                </TabsContent>
                <TabsContent value="radar">
                    <RadarSubForm
                        value={formValues.radar}
                        onChange={(v) => setValue("radar", v)}
                        registry={registry?.radar}
                    />
                </TabsContent>
                <TabsContent value="track_quality">
                    <TrackQualityMarkovSubForm
                        value={formValues.track_quality}
                        onChange={(v) => setValue("track_quality", v)}
                        registry={registry?.track_quality}
                        states={registry?.track_quality_states}
                    />
                </TabsContent>
                <TabsContent value="data_starvation">
                    <DataStarvationSubForm
                        value={formValues.data_starvation}
                        onChange={(v) => setValue("data_starvation", v)}
                    />
                </TabsContent>
                <TabsContent value="modulation">
                    <ModulationThresholdsSubForm
                        value={formValues.modulation_thresholds}
                        onChange={(v) => setValue("modulation_thresholds", v)}
                        registry={registry?.modulation_thresholds}
                    />
                </TabsContent>
            </Tabs>

            {errs.length > 0 && (
                <Alert
                    variant="destructive"
                    data-testid="degradation-editor-validation-summary"
                >
                    <AlertTriangle className="size-4" />
                    <AlertDescription>
                        <p className="font-medium">Resolve before saving:</p>
                        <ul className="mt-1 list-inside list-disc text-xs">
                            {errs.map((m, i) => (
                                <li key={i} data-testid={`degradation-editor-validation-error-${i}`}>{m}</li>
                            ))}
                        </ul>
                    </AlertDescription>
                </Alert>
            )}

            {saveErr && (
                <Alert variant="destructive" data-testid="degradation-editor-save-error">
                    <AlertTriangle className="size-4" />
                    <AlertDescription>{String(saveErr)}</AlertDescription>
                </Alert>
            )}

            <div className="flex items-center justify-between">
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/degradation-profiles")}
                    data-testid="degradation-editor-cancel"
                >
                    <ArrowLeft className="size-4" />
                    Cancel
                </Button>
                <Button
                    type="button"
                    onClick={onSave}
                    disabled={submitDisabled}
                    data-testid="degradation-editor-save"
                >
                    <Save className="size-4" />
                    {mode === "edit" ? "Save changes" : "Create profile"}
                </Button>
            </div>
        </div>
    );
}
